import {
  generateCartAttribute,
  POLAR_ATTR_KEY,
} from '@polar-analytics/pixel-sdk';
import { captureException } from '@sentry/nextjs';
import { useCart } from 'hooks/cart/useCart';
import { useScriptManagerStore } from 'hooks/useScriptManagerStore';
import Cookies from 'js-cookie';
import {
  CVG_UID_ATTRIBUTE_KEY,
  CVG_UID_COOKIE,
} from 'lib/analytics/converge/constants';
import { env } from 'lib/constants/env';
import { useCallback } from 'react';

interface CartAttribute {
  key: string;
  value: string;
}

const getPolarAttribute = async (existingPolarAttr?: string) => {
  try {
    const { encoded } = await generateCartAttribute({ existingPolarAttr });
    return { key: POLAR_ATTR_KEY, value: encoded };
  } catch (err) {
    captureException(err);
    return undefined;
  }
};

export const useCartAttributes = () => {
  const scriptManager = useScriptManagerStore((state) => state.scriptManager);
  const { data: cart } = useCart();
  const existingPolarAttr = cart?.attributes.find(
    ({ key }) => key === POLAR_ATTR_KEY,
  )?.value;

  const getCartAttributes = useCallback(async (): Promise<CartAttribute[]> => {
    const attributes: CartAttribute[] = [];

    const cvgUid = Cookies.get(CVG_UID_COOKIE);
    if (cvgUid) attributes.push({ key: CVG_UID_ATTRIBUTE_KEY, value: cvgUid });

    if (
      env.NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT &&
      scriptManager.getCondition('cookiesAccepted')
    ) {
      const polarAttribute = await getPolarAttribute(existingPolarAttr);
      if (polarAttribute) attributes.push(polarAttribute);
    }

    return attributes;
  }, [scriptManager, existingPolarAttr]);

  return { getCartAttributes };
};
